// slices/chat/pendingContextLabel.ts
//
// The context cue a user message shows before the server's own label arrives
// on `text.done`. Moved out of `chatSlice.ts`, which is at its size limit.

import type { ChatContextLabel } from '../../types/chatStream';
import { parseChatBlockPosition } from '../../services/chatBlockPosition';

const CONTEXT_LABELS: Record<ChatContextLabel['kind'], string> = {
  question: 'Question',
  flashcard: 'Flashcard',
  lecture: 'Document',
  video: 'Video',
  article: 'Article',
  exam: 'Exam',
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Where a question sits in the QBank block the envelope says the student is
 * in (ORA-1285), so a block thread can draw its "Question N" divider before
 * the server's label lands.
 */
const blockPositionFromEnvelope = (
  envelope: Record<string, unknown>
): ChatContextLabel['blockPosition'] => {
  const activity = asRecord(envelope.activity);
  if (activity?.kind !== 'qbank_block') return undefined;

  return parseChatBlockPosition(activity);
};

/**
 * The pending user row has a generic, safe cue before `text.done` supplies
 * the server-vetted title. Never display anything except a recognised type
 * from the client-owned envelope.
 *
 * A detached turn shows no cue at all (ORA-662): the anchor still travels, for
 * thread keying, but the model is not given it — so saying "Question" on the
 * student's own message would claim something Ora cannot see.
 */
export const contextLabelFromEnvelope = (
  envelope: Record<string, unknown> | null
): ChatContextLabel | undefined => {
  if (!envelope || envelope.anchorDetached === true) return undefined;

  const anchor = asRecord(envelope.anchor);
  if (!anchor) return undefined;

  const type = anchor.type;
  if (
    typeof type !== 'string' ||
    !Object.prototype.hasOwnProperty.call(CONTEXT_LABELS, type)
  )
    return undefined;

  const kind = type as ChatContextLabel['kind'];
  const id = anchor.id;

  const blockPosition =
    kind === 'question' ? blockPositionFromEnvelope(envelope) : undefined;

  return {
    kind,
    label: CONTEXT_LABELS[kind],
    ...(kind === 'lecture' && typeof id === 'string' ? { lectureId: id } : {}),
    ...(blockPosition ? { blockPosition } : {}),
  };
};
